/**
 * Recovery from stale cached script chunks.
 *
 * After a deploy, Safari (and any aggressive HTTP cache) can keep serving an old
 * HTML shell or an old module graph whose hashed chunk filenames no longer exist.
 * The dynamic import then rejects, React unmounts to the top-level error screen,
 * and the user sees "This page didn't load" on a perfectly healthy server.
 *
 * The fix is a single cache-busting reload. A sessionStorage marker guarantees we
 * never loop: if the reload doesn't help, the error screen is shown as normal.
 */

const RELOAD_MARKER = "mp:chunk-reload";

const CHUNK_ERROR_PATTERNS = [
  "failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "importing a module script failed",
  "failed to load module script",
  "unable to preload css",
  "loading chunk",
  "loading css chunk",
  "'text/html' is not a valid javascript mime type",
];

export function isStaleChunkError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? `${error.name}: ${error.message}`
      : typeof error === "string"
        ? error
        : "";
  if (!message) return false;
  const haystack = message.toLowerCase();
  return CHUNK_ERROR_PATTERNS.some((pattern) => haystack.includes(pattern));
}

function markerStore(): Storage | null {
  try {
    return window.sessionStorage ?? null;
  } catch {
    return null;
  }
}

function alreadyTried(): boolean {
  try {
    return markerStore()?.getItem(RELOAD_MARKER) === "1";
  } catch {
    return false;
  }
}

function markTried(): void {
  try {
    markerStore()?.setItem(RELOAD_MARKER, "1");
  } catch {
    /* storage blocked — worst case we skip the retry below */
  }
}

/** Clear the guard once the app has rendered successfully. */
export function clearStaleChunkMarker(): void {
  if (typeof window === "undefined") return;
  try {
    markerStore()?.removeItem(RELOAD_MARKER);
  } catch {
    /* ignore */
  }
}

/**
 * Reloads once, bypassing the cached HTML shell, when `error` looks like a stale
 * chunk. Returns true when a reload was triggered so callers can skip rendering.
 */
export function recoverFromStaleChunk(error: unknown): boolean {
  if (typeof window === "undefined") return false;
  if (!isStaleChunkError(error)) return false;
  if (alreadyTried()) return false;

  markTried();
  try {
    const url = new URL(window.location.href);
    url.searchParams.set("r", Date.now().toString(36));
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
  return true;
}

let listenersInstalled = false;

/** Catches chunk failures that happen outside a React error boundary. */
export function installStaleChunkRecovery(): void {
  if (typeof window === "undefined" || listenersInstalled) return;
  listenersInstalled = true;

  window.addEventListener("error", (event) => {
    recoverFromStaleChunk(event.error ?? event.message);
  });
  window.addEventListener("unhandledrejection", (event) => {
    recoverFromStaleChunk(event.reason);
  });
}
